import { useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Label, Pie, PieChart, XAxis, YAxis } from 'recharts';
import type { RouteDefinition } from '@/routes';
import { applyRouteMeta } from '@/lib/meta';
import { AppLayout } from '@/layout/AppLayout';
import { AssumptionPanel } from '@/components/calculator/AssumptionPanel';
import { ChartCard } from '@/components/calculator/ChartCard';
import { CalculatorSeoArticle } from '@/components/calculator/CalculatorSeoArticle';
import { FieldExplainerGuide } from '@/components/calculator/FieldExplainerGuide';
import { NumberField } from '@/components/calculator/NumberField';
import { ResultSummaryCard } from '@/components/calculator/ResultSummaryCard';
import { ShareExportRow } from '@/components/calculator/ShareExportRow';
import { RouteNextSteps } from '@/components/content/RouteNextSteps';
import { Badge } from '@/components/ui/badge';
import { ChartContainer, ChartLegendPills, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { getBudgetPlannerFieldExplainers } from '@/content/field-explainers/budget-planner';
import type { FieldExplainerContent } from '@/content/field-explainers/types';
import { calculateBudgetPlanner, type BudgetPlannerInput } from '@/engines/legacy-engines';
import {
  copyToClipboard,
  createContractShareUrl,
  exportPdfReport,
  exportRowsToCsv,
  formatCurrency,
  parseContractState
} from './shared';

type BudgetState = {
  salaryIncome: number;
  sideIncome: number;
  housing: number;
  utilities: number;
  groceries: number;
  transportation: number;
  insurance: number;
  entertainment: number;
  dining: number;
  savings: number;
};

const defaults: BudgetState = {
  salaryIncome: 5000,
  sideIncome: 0,
  housing: 1500,
  utilities: 220,
  groceries: 450,
  transportation: 500,
  insurance: 300,
  entertainment: 180,
  dining: 220,
  savings: 800
};

const categories: BudgetPlannerInput['categories'] = [
  { id: 'housing', type: 'needs' },
  { id: 'utilities', type: 'needs' },
  { id: 'groceries', type: 'needs' },
  { id: 'transportation', type: 'needs' },
  { id: 'insurance', type: 'needs' },
  { id: 'entertainment', type: 'wants' },
  { id: 'dining', type: 'wants' },
  { id: 'savings', type: 'savings' }
];


const categoryLabelByType: Record<string, string> = {
  needs: 'Needs',
  wants: 'Wants',
  savings: 'Savings'
};

const barColorsByType: Record<string, string> = {
  needs: 'var(--color-needs)',
  wants: 'var(--color-wants)',
  savings: 'var(--color-savings)'
};

const chartConfig = {
  needs: {
    label: 'Needs',
    color: 'var(--chart-1)'
  },
  wants: {
    label: 'Wants',
    color: 'var(--chart-3)'
  },
  savings: {
    label: 'Savings',
    color: 'var(--chart-2)'
  },
  amount: {
    label: 'Amount',
    color: 'var(--chart-4)'
  }
} satisfies ChartConfig;

type Props = { route: RouteDefinition };

function toEngineInput(state: BudgetState): BudgetPlannerInput {
  return {
    incomes: [{ amount: state.salaryIncome }, { amount: state.sideIncome }],
    expenses: [
      { category: 'housing', amount: state.housing },
      { category: 'utilities', amount: state.utilities },
      { category: 'groceries', amount: state.groceries },
      { category: 'transportation', amount: state.transportation },
      { category: 'insurance', amount: state.insurance },
      { category: 'entertainment', amount: state.entertainment },
      { category: 'dining', amount: state.dining },
      { category: 'savings', amount: state.savings }
    ],
    categories
  };
}

function formatCompactCurrency(value: number): string {
  if (!Number.isFinite(value)) {
    return '$0';
  }

  if (Math.abs(value) < 1000) {
    return `$${Math.round(value)}`;
  }

  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 1
  }).format(value);
}

export function BudgetPlannerCalculatorPage({ route }: Props) {
  const { toast } = useToast();
  const [state, setState] = useState<BudgetState>(() => {
    const parsed = parseContractState('budgetPlanner', {
      incomes: [{ amount: defaults.salaryIncome }, { amount: defaults.sideIncome }],
      expenses: Object.keys(defaults)
        .filter((key) => key !== 'salaryIncome' && key !== 'sideIncome')
        .map((key) => ({ category: key, amount: defaults[key as keyof BudgetState] as number }))
    });

    const expenseLookup = new Map((parsed.expenses ?? []).map((expense) => [String(expense.category), Number(expense.amount ?? 0)]));
    const incomes = Array.isArray(parsed.incomes) ? parsed.incomes : [];

    return {
      salaryIncome: Number(incomes[0]?.amount ?? defaults.salaryIncome),
      sideIncome: Number(incomes[1]?.amount ?? defaults.sideIncome),
      housing: expenseLookup.get('housing') ?? defaults.housing,
      utilities: expenseLookup.get('utilities') ?? defaults.utilities,
      groceries: expenseLookup.get('groceries') ?? defaults.groceries,
      transportation: expenseLookup.get('transportation') ?? defaults.transportation,
      insurance: expenseLookup.get('insurance') ?? defaults.insurance,
      entertainment: expenseLookup.get('entertainment') ?? defaults.entertainment,
      dining: expenseLookup.get('dining') ?? defaults.dining,
      savings: expenseLookup.get('savings') ?? defaults.savings
    };
  });

  useEffect(() => {
    applyRouteMeta(route);
  }, [route]);

  const input = useMemo(() => toEngineInput(state), [state]);
  const result = useMemo(() => calculateBudgetPlanner(input), [input]);

  const explainers = useMemo(() => {
    const entries = getBudgetPlannerFieldExplainers({
      input: {
        salaryIncome: state.salaryIncome,
        sideIncome: state.sideIncome,
        housing: state.housing,
        utilities: state.utilities,
        groceries: state.groceries,
        transportation: state.transportation,
        insurance: state.insurance,
        entertainment: state.entertainment,
        dining: state.dining,
        savings: state.savings
      },
      result: {
        totalIncome: result.totalIncome,
        totalExpenses: result.totalExpenses,
        remaining: result.remaining,
        needsTotal: result.needsTotal,
        wantsTotal: result.wantsTotal,
        savingsTotal: result.savingsTotal,
        needsPercent: result.needsPercent,
        wantsPercent: result.wantsPercent,
        savingsPercent: result.savingsPercent
      }
    });
    const byId = new Map<string, FieldExplainerContent>(entries.map((entry) => [entry.id, entry]));
    return { entries, byId };
  }, [state, result]);

  const categoryRows = useMemo(
    () =>
      categories.map((category) => ({
        id: category.id,
        type: category.type,
        typeLabel: categoryLabelByType[category.type],
        amount: result.categoryTotals[category.id] ?? 0
      })),
    [result.categoryTotals]
  );

  const allocationData = [
    { key: 'needs', label: 'Needs', value: result.needsPercent },
    { key: 'wants', label: 'Wants', value: result.wantsPercent },
    { key: 'savings', label: 'Savings', value: result.savingsPercent }
  ];

  const shareState = {
    incomes: input.incomes,
    expenses: input.expenses
  };

  return (
    <AppLayout>
      <section className="hero-shell rounded-2xl border border-border p-6 md:p-8">
        <h1 className="font-display text-4xl leading-tight md:text-5xl">Budget Planner</h1>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground md:text-base">
          Build a monthly budget with category-level allocations and a clear needs/wants/savings mix.
        </p>
      </section>

      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="before" />

      <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <AssumptionPanel title="Income and spending" description="Adjust monthly income streams and category allocations.">
          <div className="grid gap-4 md:grid-cols-2">
            <NumberField id="budgetSalary" label="Salary income" value={state.salaryIncome} min={0} step={100} prefix="$" explainer={explainers.byId.get('salaryIncome')} onChange={(value) => setState((prev) => ({ ...prev, salaryIncome: value }))} />
            <NumberField id="budgetSideIncome" label="Side income" value={state.sideIncome} min={0} step={100} prefix="$" explainer={explainers.byId.get('sideIncome')} onChange={(value) => setState((prev) => ({ ...prev, sideIncome: value }))} />
            <NumberField id="budgetHousing" label="Housing" value={state.housing} min={0} step={25} prefix="$" explainer={explainers.byId.get('housing')} onChange={(value) => setState((prev) => ({ ...prev, housing: value }))} />
            <NumberField id="budgetUtilities" label="Utilities" value={state.utilities} min={0} step={10} prefix="$" explainer={explainers.byId.get('utilities')} onChange={(value) => setState((prev) => ({ ...prev, utilities: value }))} />
            <NumberField id="budgetGroceries" label="Groceries" value={state.groceries} min={0} step={10} prefix="$" explainer={explainers.byId.get('groceries')} onChange={(value) => setState((prev) => ({ ...prev, groceries: value }))} />
            <NumberField id="budgetTransportation" label="Transportation" value={state.transportation} min={0} step={10} prefix="$" explainer={explainers.byId.get('transportation')} onChange={(value) => setState((prev) => ({ ...prev, transportation: value }))} />
            <NumberField id="budgetInsurance" label="Insurance" value={state.insurance} min={0} step={10} prefix="$" explainer={explainers.byId.get('insurance')} onChange={(value) => setState((prev) => ({ ...prev, insurance: value }))} />
            <NumberField id="budgetEntertainment" label="Entertainment" value={state.entertainment} min={0} step={10} prefix="$" explainer={explainers.byId.get('entertainment')} onChange={(value) => setState((prev) => ({ ...prev, entertainment: value }))} />
            <NumberField id="budgetDining" label="Dining" value={state.dining} min={0} step={10} prefix="$" explainer={explainers.byId.get('dining')} onChange={(value) => setState((prev) => ({ ...prev, dining: value }))} />
            <NumberField id="budgetSavings" label="Savings" value={state.savings} min={0} step={10} prefix="$" explainer={explainers.byId.get('savings')} onChange={(value) => setState((prev) => ({ ...prev, savings: value }))} />
          </div>
        </AssumptionPanel>

        <div className="space-y-4">
          <ResultSummaryCard label="Total income" value={formatCurrency(result.totalIncome)} note="monthly take-home modeled in planner" trend="flat" explainer={explainers.byId.get('totalIncome')} />
          <ResultSummaryCard label="Total expenses" value={formatCurrency(result.totalExpenses)} note="sum of all category allocations" trend="flat" explainer={explainers.byId.get('totalExpenses')} />
          <ResultSummaryCard label="Remaining cash flow" value={formatCurrency(result.remaining)} note={result.remaining >= 0 ? 'positive monthly buffer' : 'budget overspend detected'} trend={result.remaining >= 0 ? 'up' : 'down'} explainer={explainers.byId.get('remaining')} />
          <ResultSummaryCard label="50/30/20 snapshot" value={`${result.needsPercent}/${result.wantsPercent}/${result.savingsPercent}`} note="needs/wants/savings percentages" trend="flat" explainer={explainers.byId.get('allocationMix')} />

          <ShareExportRow
            onCopyShare={async () => {
              const shareUrl = createContractShareUrl(route.canonicalPath, 'budgetPlanner', shareState);
              const ok = await copyToClipboard(shareUrl);
              toast({ title: ok ? 'Share link copied' : 'Share link ready', description: ok ? shareUrl : `Copy manually: ${shareUrl}`, variant: ok ? 'success' : 'warning' });
            }}
            onExportCsv={() => {
              const headers = ['Category', 'Type', 'Amount'];
              const rows = categoryRows.map((row) => [row.id, row.typeLabel, Math.round(row.amount)]);
              exportRowsToCsv(headers, rows, 'budget-planner-breakdown.csv');
              toast({ title: 'CSV exported', description: 'budget-planner-breakdown.csv downloaded.', variant: 'success' });
            }}
            onExportPdf={async () => {
              const ok = await exportPdfReport({
                title: 'Budget Planner Breakdown',
                subtitle: 'Monthly budget allocation',
                summaryLeft: [
                  `Total income: ${formatCurrency(result.totalIncome)}`,
                  `Total expenses: ${formatCurrency(result.totalExpenses)}`,
                  `Remaining: ${formatCurrency(result.remaining)}`
                ],
                summaryRight: [
                  `Needs: ${result.needsPercent}%`,
                  `Wants: ${result.wantsPercent}%`,
                  `Savings: ${result.savingsPercent}%`
                ],
                headers: ['Category', 'Type', 'Amount'],
                rows: categoryRows.map((row) => [row.id, row.typeLabel, formatCurrency(row.amount)]),
                filename: 'budget-planner-breakdown.pdf'
              });

              toast({
                title: ok ? 'PDF exported' : 'PDF export unavailable',
                description: ok ? 'budget-planner-breakdown.pdf downloaded.' : 'PDF dependencies are not available right now.',
                variant: ok ? 'success' : 'warning'
              });
            }}
          />
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <ChartCard
          title="Category amounts"
          description="Dollar allocation by budget category."
          annotation={
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span>
                Total income <span className="font-semibold text-foreground">{formatCurrency(result.totalIncome)}</span>
              </span>
              <span>
                Remaining cash flow <span className="font-semibold text-foreground">{formatCurrency(result.remaining)}</span>
              </span>
            </div>
          }
          legend={<ChartLegendPills config={chartConfig} keys={['needs', 'wants', 'savings']} />}
        >
          <ChartContainer config={chartConfig} family="comparison" className="h-64 sm:h-72">
            <BarChart
              accessibilityLayer
              data={categoryRows}
              margin={{
                top: 18,
                left: 12,
                right: 12
              }}
            >
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="id"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                interval={0}
                minTickGap={20}
                tickFormatter={(value) => String(value).replace(/^(.)/, (letter) => letter.toUpperCase())}
              />
              <YAxis tickLine={false} axisLine={false} tickMargin={8} width={70} tickFormatter={(value) => formatCompactCurrency(Number(value))} />
              <ChartTooltip cursor={false} content={<ChartTooltipContent formatter={(value) => formatCurrency(Number(value ?? 0))} />} />
              <Bar dataKey="amount" radius={4}>
                {categoryRows.map((row) => (
                  <Cell key={row.id} fill={barColorsByType[row.type] ?? 'var(--color-amount)'} />
                ))}
              </Bar>
            </BarChart>
          </ChartContainer>
        </ChartCard>

        <ChartCard
          title="Allocation mix"
          description="Needs, wants, and savings percentage split."
          annotation={
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span>
                Needs <span className="font-semibold text-foreground">{result.needsPercent}%</span>
              </span>
              <span>
                Wants <span className="font-semibold text-foreground">{result.wantsPercent}%</span>
              </span>
              <span>
                Savings <span className="font-semibold text-foreground">{result.savingsPercent}%</span>
              </span>
            </div>
          }
          legend={<ChartLegendPills config={chartConfig} keys={['needs', 'wants', 'savings']} />}
        >
          <ChartContainer config={chartConfig} family="allocation" className="h-64 sm:h-72">
            <PieChart accessibilityLayer>
              <Pie data={allocationData} dataKey="value" nameKey="label" innerRadius={60} outerRadius={95} strokeWidth={2}>
                <Label
                  content={({ viewBox }) => {
                    if (!viewBox || !('cx' in viewBox) || !('cy' in viewBox)) {
                      return null;
                    }

                    return (
                      <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                        <tspan x={viewBox.cx} y={viewBox.cy - 10} className="fill-muted-foreground text-[11px] uppercase tracking-[0.16em]">
                          Mix
                        </tspan>
                        <tspan x={viewBox.cx} y={viewBox.cy + 14} className="fill-foreground font-display text-[20px]">
                          {`${result.needsPercent}/${result.wantsPercent}/${result.savingsPercent}`}
                        </tspan>
                      </text>
                    );
                  }}
                />
                {allocationData.map((entry) => (
                  <Cell key={entry.key} fill={`var(--color-${entry.key})`} />
                ))}
              </Pie>
              <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel formatter={(value, key) => `${Number(value ?? 0).toFixed(1)}% (${key})`} />} />
            </PieChart>
          </ChartContainer>
        </ChartCard>
      </section>

      <section>
        <ChartCard title="Category table" description="Category-level amounts generated by the shared engine.">
          <Table containerClassName="max-h-[28rem]" stickyHeader compact>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead>Type</TableHead>
                <TableHead numeric>Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categoryRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="normal-case tracking-[0.04em]">
                      {row.typeLabel}
                    </Badge>
                  </TableCell>
                  <TableCell numeric>{formatCurrency(row.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ChartCard>
      </section>

      <section>
        <FieldExplainerGuide
          title="How each number is calculated"
          description="Every input and result on this page, explained with your numbers filled in. Tap the info icon next to any field for the same explanation in place."
          entries={explainers.entries}
        />
      </section>

      <RouteNextSteps canonicalPath={route.canonicalPath} />
      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="after" />
    </AppLayout>
  );
}
